import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import toast from 'react-hot-toast';

interface Stats    { totalQuotes: number; totalValue: number; quotesWon: number; closeRate: number; }
interface Quote    { id: string; jobScope: string; totalAmount: number; status: string; createdAt: string; pdfUrl?: string; }
interface Template { id: string; name: string; rateAmount: number; rateUnit: string; }
interface Customer { id: string; name: string; whatsapp?: string; email?: string; }
interface LineItem { templateId: string; templateName: string; jobScope: string; }

const card: any = { background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };
const fmt = (c: number) => `$${(c / 100).toFixed(2)}`;
const statusStyle = (s: string): any =>
  s === 'won'  ? { background: '#dcfce7', color: '#16a34a' } :
  s === 'sent' ? { background: '#dbeafe', color: '#1d4ed8' } :
                 { background: '#f3f4f6', color: '#555' };

export default function Dashboard() {
  const [stats, setStats]         = useState<Stats | null>(null);
  const [quotes, setQuotes]       = useState<Quote[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError]         = useState('');
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    api.get('/quotes/stats/dashboard').then(r => setStats(r.data)).catch(console.error);
    api.get('/quotes').then(r => setQuotes(r.data.slice(0, 5))).catch(console.error);
    api.get('/templates').then(r => setTemplates(r.data)).catch(console.error);
  }, []);

  // Add a new empty line item for a service
  const addService = (t: Template) => {
    // If already added, don't add duplicate
    if (lineItems.find(li => li.templateId === t.id)) {
      toast('Already added. Edit the description below.', { icon: '👇' });
      return;
    }
    setLineItems(prev => [...prev, { templateId: t.id, templateName: t.name, jobScope: '' }]);
  };

  const removeService = (templateId: string) => {
    setLineItems(prev => prev.filter(li => li.templateId !== templateId));
  };

  const updateScope = (templateId: string, value: string) => {
    setLineItems(prev => prev.map(li => li.templateId === templateId ? { ...li, jobScope: value } : li));
  };

  const generate = async () => {
    if (lineItems.length === 0)
      return toast.error('Add at least one service first');
    const empty = lineItems.find(li => !li.jobScope.trim());
    if (empty)
      return toast.error(`Please describe the "${empty.templateName}" job`);

    setGenerating(true);
    setError('');
    try {
      // If single service — use existing single-quote endpoint
      if (lineItems.length === 1) {
        const { data } = await api.post('/quotes', {
          jobScope:   lineItems[0].jobScope,
          templateId: lineItems[0].templateId
        });
        toast.success('Quote generated!');
        setQuotes(p => [data, ...p.slice(0, 4)]);
        setLineItems([]);
        setStats(s => s ? { ...s, totalQuotes: s.totalQuotes + 1, totalValue: s.totalValue + data.totalAmount } : s);
      } else {
        // Multiple services — use multi-quote endpoint
        const { data } = await api.post('/quotes/multi', { lineItems });
        toast.success('Multi-service quote generated!');
        setQuotes(p => [data, ...p.slice(0, 4)]);
        setLineItems([]);
        setStats(s => s ? { ...s, totalQuotes: s.totalQuotes + 1, totalValue: s.totalValue + data.totalAmount } : s);
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || err.message || 'Something went wrong';
      setError(msg);
      toast.error(msg);
      console.error('Full error:', err.response || err);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6' }}>
      <nav style={{ background: 'white', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: '#2563EB' }}>QuotationBot 🤖</h1>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <Link to="/quotes"   style={{ color: '#555', textDecoration: 'none' }}>Quotes</Link>
          <Link to="/settings" style={{ color: '#555', textDecoration: 'none' }}>Settings</Link>
          <button onClick={() => { localStorage.clear(); navigate('/login'); }}
            style={{ background: 'none', border: '1px solid #ddd', padding: '6px 14px', borderRadius: 6, cursor: 'pointer' }}>Logout</button>
        </div>
      </nav>

      <div style={{ maxWidth: 900, margin: '32px auto', padding: '0 24px' }}>
        <h2 style={{ marginBottom: 24 }}>Welcome, {user.businessName || 'there'}! 👋</h2>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
          {([
            ['Quotes This Month', stats?.totalQuotes ?? '—'],
            ['Total Value',       stats ? fmt(stats.totalValue) : '—'],
            ['Jobs Won',          stats?.quotesWon ?? '—'],
            ['Close Rate',        stats ? `${stats.closeRate}%` : '—']
          ] as [string,any][]).map(([label, value]) => (
            <div key={label} style={card}>
              <div style={{ color: '#666', fontSize: 13, marginBottom: 8 }}>{label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#2563EB' }}>{value}</div>
            </div>
          ))}
        </div>

        {/* Generate Quote */}
        <div style={{ ...card, marginBottom: 24 }}>
          <h3 style={{ marginBottom: 4 }}>⚡ Generate New Quote</h3>
          <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
            Pick one or more services for this job — Claude will combine them into one quote.
          </p>

          {/* No rate cards warning */}
          {templates.length === 0 && (
            <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#92400e', fontSize: 14 }}>
              You have no rate cards yet.{' '}
              <Link to="/settings" style={{ color: '#92400e', fontWeight: 700 }}>Add one in Settings →</Link>
            </div>
          )}

          {/* Error box */}
          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', marginBottom: 16, color: '#dc2626', fontSize: 14 }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Step 1: Pick services */}
          {templates.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 10, fontWeight: 600, fontSize: 14, color: '#374151' }}>
                Step 1 — Select services for this job
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {templates.map(t => {
                  const added = !!lineItems.find(li => li.templateId === t.id);
                  return (
                    <button
                      key={t.id}
                      onClick={() => added ? removeService(t.id) : addService(t)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 8,
                        border: `2px solid ${added ? '#2563EB' : '#e5e7eb'}`,
                        background: added ? '#dbeafe' : 'white',
                        color: added ? '#1d4ed8' : '#555',
                        fontWeight: added ? 700 : 400,
                        cursor: 'pointer',
                        fontSize: 14,
                        transition: 'all 0.15s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}>
                      {added ? '✓ ' : '+ '}{t.name}
                      <span style={{ fontSize: 12, opacity: 0.7 }}>${t.rateAmount / 100}/{t.rateUnit}</span>
                    </button>
                  );
                })}
                <Link to="/settings"
                  style={{ padding: '8px 16px', borderRadius: 8, border: '2px dashed #e5e7eb', color: '#999', fontSize: 14, textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                  + Add service type
                </Link>
              </div>
            </div>
          )}

          {/* Step 2: Describe each selected service */}
          {lineItems.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', marginBottom: 10, fontWeight: 600, fontSize: 14, color: '#374151' }}>
                Step 2 — Describe each service
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {lineItems.map(li => (
                  <div key={li.templateId} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontWeight: 700, color: '#2563EB', fontSize: 14 }}>{li.templateName}</span>
                      <button onClick={() => removeService(li.templateId)}
                        style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 13 }}>
                        ✕ Remove
                      </button>
                    </div>
                    <textarea
                      value={li.jobScope}
                      onChange={e => updateScope(li.templateId, e.target.value)}
                      placeholder={`Describe the ${li.templateName} work... e.g. "3 bedrooms, 2 coats, with primer"`}
                      style={{ width: '100%', height: 70, padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Generate button */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={generate}
              disabled={generating || lineItems.length === 0 || lineItems.some(li => !li.jobScope.trim())}
              style={{
                padding: '12px 28px',
                background: generating || lineItems.length === 0 ? '#94a3b8' : '#2563EB',
                color: 'white', border: 'none', borderRadius: 8,
                fontSize: 15, fontWeight: 600,
                cursor: generating || lineItems.length === 0 ? 'not-allowed' : 'pointer'
              }}>
              {generating ? '⏳ Generating...' : `✨ Generate Quote${lineItems.length > 1 ? ` (${lineItems.length} services)` : ''}`}
            </button>
            {generating && <span style={{ fontSize: 13, color: '#666' }}>Calling Claude AI... 5–15 seconds</span>}
          </div>
        </div>

        {/* Recent Quotes */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3>Recent Quotes</h3>
            <Link to="/quotes" style={{ color: '#2563EB', textDecoration: 'none', fontSize: 14 }}>View all →</Link>
          </div>
          {quotes.length === 0
            ? <p style={{ color: '#999', textAlign: 'center', padding: 24 }}>No quotes yet. Generate your first one above! ☝️</p>
            : quotes.map(q => (
              <div key={q.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f0f0f0' }}>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>{q.jobScope.substring(0, 60)}{q.jobScope.length > 60 ? '...' : ''}</div>
                  <div style={{ fontSize: 13, color: '#999' }}>{new Date(q.createdAt).toLocaleDateString()}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, color: '#2563EB', marginBottom: 4 }}>{fmt(q.totalAmount)}</div>
                  <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 12, ...statusStyle(q.status) }}>{q.status}</span>
                  {q.pdfUrl && (
                    <div style={{ marginTop: 4 }}>
                      <a href={q.pdfUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#2563EB' }}>View PDF</a>
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}